import React, { useEffect } from 'react';
import { styled } from 'styled-components';
import calendar from '@ingka/ssr-icon/paths/calendar';
import Text from '@ingka/text';
import Button from '@ingka/button';
import storeIcon from '@ingka/ssr-icon/paths/store';
import home from '@ingka/ssr-icon/paths/home';
import videoCamera from '@ingka/ssr-icon/paths/video-camera';
import { useTranslation } from 'react-i18next';
import colors from '../styles/colors';
import PageContent from './PageContent';
import {
  isServiceProvidedRemote,
  type ServiceLocation,
} from '../helpers/appHelper';
import { useStateSelector } from '../store/';
import InfoMessage from './InfoMessage';
import CoworkerImage from './CoworkerImage';
import StickyFooter from './StickyFooter';
import PayOrderButton from './PayOrderButton';
import { StyledWrapper } from './PageLayout';
import TextWithPrice from './TextWithPrice';
import RedirectButton from './RedirectButton';
import { getQueryParamsFromURL } from '../helpers/urlHelper';
import { PayOrderPrice } from './PayOrder';
import { sendEventToParent } from '../eventPublisher';
import type { AvailableService } from '../services/appointment/model';
import type { AppView } from '../types/app';

interface ConfirmDetailsWithStickyFooterProps {
  orderNo: string;
  service: AvailableService | undefined;
  serviceLocation: ServiceLocation | undefined;
  showPayOrder: boolean;
  timeText: string;
  addToCalendar: React.ReactNode;
  locationTypeIcon: string;
  locationTypeMessage: string;
  view: AppView;
}

const ImageWrapper = styled.div`
  padding: 1.5rem 0;
`;

const DetailsItem = styled.div`
  margin-bottom: 12px;
`;

const getIcon = (locationTypeIcon: string) => {
  switch (locationTypeIcon) {
    case 'store':
      return storeIcon;
    case 'home':
      return home;
    case 'remote':
      return videoCamera;
    default:
      return storeIcon;
  }
};

const ConfirmDetailsWithStickyFooter: React.FC<
  ConfirmDetailsWithStickyFooterProps
> = ({
  orderNo,
  service,
  serviceLocation,
  showPayOrder,
  timeText,
  addToCalendar,
  locationTypeIcon,
  locationTypeMessage,
  view,
}) => {
  const { t } = useTranslation();
  const redirectUrl = getQueryParamsFromURL().redirectUrl;
  const refApp = getQueryParamsFromURL().refApp;
  const contact = useStateSelector((state) => state.contact);
  const payOrderLink = useStateSelector(
    (state) => state.app.booking.payOrderLink,
  );
  const isHomeProjects = refApp === 'home_projects';

  const renderButtonText = () => {
    return service?.customerPrice ? (
      <TextWithPrice
        key="regularPriceContainer"
        alignment="right"
        price={service.customerPrice.priceFormatted}
        textKey="pages.confirm.payOrderButtonTextWithPrice"
        textColor={colors.static.white}
      />
    ) : (
      t('pages.confirm.payOrderButtonText')
    );
  };

  useEffect(() => {
    const inactivateBackButton = () => {
      // Push current location to history
      window.history.pushState(null, '', window.location.href);
      // When clicking back, it will go forward and landing on current page
      window.onpopstate = () => {
        window.history.go(1);
      };
    };

    if (view === 'app') inactivateBackButton();
  }, [view]);

  return (
    <StyledWrapper $withStickyFooter>
      <PageContent>
        <Text tagName={view === 'alt' ? 'h2' : 'h1'} textStyle="Heading.L">
          {t('pages.confirm.title')}
        </Text>
        <Text tagName="p" textStyle="Body.L">
          {t('pages.confirm.description')}
        </Text>
        <ImageWrapper>
          <CoworkerImage />
        </ImageWrapper>
        <DetailsItem>
          <InfoMessage
            icon={calendar}
            iconAriaLabel={t('pages.confirm.icons.dateAndTime')}
            message={[timeText]}
            id="service-time"
            title={t('pages.confirm.dateAndTime')}
          />
        </DetailsItem>
        <DetailsItem>
          {view !== 'app' && addToCalendar && (
            <DetailsItem>{addToCalendar}</DetailsItem>
          )}
        </DetailsItem>
        <DetailsItem>
          {serviceLocation && (
            <InfoMessage
              icon={getIcon(locationTypeIcon)}
              iconAriaLabel={t('pages.confirm.icons.location')}
              message={[locationTypeMessage]}
              id={
                isServiceProvidedRemote(serviceLocation)
                  ? 'service-location-remote'
                  : 'service-location'
              }
              title={t('pages.confirm.typeOfAppointment.title')}
            />
          )}
        </DetailsItem>
        <DetailsItem>
          {view === 'app' && showPayOrder && service && (
            <PayOrderPrice
              price={service.customerPrice}
              currencyCode={service.displayPrice.currencyCode}
            />
          )}
        </DetailsItem>

        <StickyFooter>
          {view !== 'app' && showPayOrder && payOrderLink ? (
            <PayOrderButton
              payOrderLink={payOrderLink}
              orderNo={orderNo}
              email={contact.email}
              buttonText={renderButtonText()}
              view={view}
            />
          ) : view === 'app' ? (
            <RedirectButton
              dataCy="pages-confirm-back-button"
              url={`${redirectUrl}?status=success`}
            />
          ) : (
            <Button
              text={
                isHomeProjects
                  ? t('components.stickyFooter.returnAndCloseButton')
                  : t('components.stickyFooter.doneButton')
              }
              type="emphasised"
              data-cy="confirm-and-close-button"
              onClick={() => {
                if (view === 'alt') {
                  sendEventToParent('BOKA/BOKA_CLOSED', view);
                }
              }}
            />
          )}
        </StickyFooter>
      </PageContent>
    </StyledWrapper>
  );
};

export default ConfirmDetailsWithStickyFooter;
